<template>
    <div v-if="showConsent" ref="consentRef" class="cookie-consent">
        <div ref="contentRef" class="cookie-consent__content">
            <div ref="textRef" class="cookie-consent__text">
                <p>
                This website uses cookies to analyze traffic and improve your experience. I use a privacy-focused analytics tool to understand how visitors interact with my content. Your continued use implies consent.
                </p>
            </div>
            <div ref="actionsRef" class="cookie-consent__actions">
                <button @click="acceptCookies" class="cookie-consent__button cookie-consent__button--accept">
                    Accept All
                </button>
                <button @click="declineCookies" class="cookie-consent__button cookie-consent__button--decline">
                    Accept Only Necessary 
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, onMounted, nextTick } from 'vue';
import { gsap } from 'gsap';

export default {
    name: 'CookieConsent',
    setup() {
        const showConsent = ref(false);
        const consentRef = ref(null);
        const contentRef = ref(null);
        const textRef = ref(null);
        const actionsRef = ref(null);
        const CONSENT_KEY = 'cookie-consent';

        const initMatomo = () => {
            if (typeof window._paq === 'undefined') {
                window._paq = window._paq || [];
            }

            // Configure Matomo
            const _paq = window._paq;
            _paq.push(['trackPageView']);
            _paq.push(['enableLinkTracking']);

            // Matomo configuration
            const matomoUrl = 'https://tracking.agency-19.com/';
            const siteId = '4'; // Update this with your actual site ID from Matomo

            _paq.push(['setTrackerUrl', matomoUrl + 'matomo.php']);
            _paq.push(['setSiteId', siteId]);

            // Load Matomo script
            const script = document.createElement('script');
            script.async = true;
            script.src = matomoUrl + 'matomo.js';
            document.head.appendChild(script);
        };

        const disableMatomo = () => {
            if (window._paq) {
                window._paq.push(['optUserOut']);
            }
        };

        const animateIn = () => {
            if (!consentRef.value || !textRef.value || !actionsRef.value) return;

            const tl = gsap.timeline();

            // Initial state
            gsap.set(consentRef.value, { y: '100%', opacity: 0 });
            gsap.set(textRef.value, { y: 20, opacity: 0 });
            gsap.set(actionsRef.value, { y: 20, opacity: 0 });

            // Animate in
            tl.to(consentRef.value, {
                y: '0%',
                opacity: 1,
                duration: 0.6,
                ease: 'power3.out'
            })
            .to(textRef.value, {
                y: 0,
                opacity: 1,
                duration: 0.4,
                ease: 'power2.out'
            }, '-=0.3')
            .to(actionsRef.value, {
                y: 0,
                opacity: 1,
                duration: 0.4,
                ease: 'power2.out'
            }, '-=0.2');
        };

        const animateOut = (callback) => {
            if (!consentRef.value) return;

            gsap.to(consentRef.value, {
                y: '100%',
                opacity: 0,
                duration: 0.4,
                ease: 'power3.in',
                onComplete: () => {
                    showConsent.value = false;
                    if (callback) callback();
                }
            });
        };

        const acceptCookies = () => {
            localStorage.setItem(CONSENT_KEY, 'accepted');
            animateOut(() => initMatomo());
        };

        const declineCookies = () => {
            localStorage.setItem(CONSENT_KEY, 'declined');
            animateOut(() => disableMatomo());
        };

        onMounted(async () => {
            const consent = localStorage.getItem(CONSENT_KEY);

            if (!consent) {
                showConsent.value = true;
                await nextTick();
                animateIn();
            } else if (consent === 'accepted') {
                initMatomo();
            }
        });

        return {
            showConsent,
            consentRef,
            contentRef,
            textRef,
            actionsRef,
            acceptCookies,
            declineCookies
        };
    }
};
</script>

<style lang="scss" scoped>
.cookie-consent {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: var(--color-white);
    // border-top: 1px solid var(--color-black);
    padding: rem(50px 25px);
    z-index: 9999;
    box-shadow: 0 rem(-4px) rem(16px) rgba(0, 0, 0, 0.1);

    &__content {
        max-width: rem(1440px);
        margin: 0 auto;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: rem(24px);
        flex-wrap: wrap;

        @media (max-width: 768px) {
            flex-direction: column;
            align-items: flex-start;
        }
    }

    &__text {
        flex: 1;

        p {
            margin: 0;
            font-size: rem(16px);
            line-height: rem(24px);
            color: var(--text-color);
            font-family: var(--default-font);
        }
    }

    &__actions {
        display: flex;
        gap: rem(12px);
        flex-wrap: wrap;
    }

    &__button {
        background-color: var(--color-black);
        border: none;
        border-radius: rem(50px);
        color: var(--color-white);
        cursor: pointer;
        display: inline-block;
        font-family: var(--default-font);
        font-size: rem(16px);
        line-height: rem(24px);
        padding: rem(12px 32px);
        transition: var(--default-transition);
        will-change: background-color, color;

        &:hover {
            background-color: var(--link-color);
        }

        &--decline {
            background-color: transparent;
            color: var(--color-black);
            border: 1px solid var(--color-black);

            &:hover {
                background-color: var(--color-black);
                color: var(--color-white);
            }
        }
    }
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-enter-from {
    opacity: 0;
    transform: translateY(100%);
}

.fade-leave-to {
    opacity: 0;
    transform: translateY(100%);
}

.fade-enter-to,
.fade-leave-from {
    opacity: 1;
    transform: translateY(0);
}
</style>
